import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getProducts } from '../services/api';

const Home = ({ onAddToCart }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    const data = await getProducts();
    setProducts(data);
    setLoading(false);
  };

  const featuredProducts = products.filter(p => p.badge === 'Bestseller').slice(0, 3);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Cleaner, More Spacious */}
      <section className="relative bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="max-w-xl">
              <h1 className="text-6xl font-light text-neutral-900 mb-8 leading-tight">
                Where Style Meets
                <span className="block font-normal mt-2">Tail-Wagging Comfort</span>
              </h1>
              <p className="text-lg text-neutral-600 mb-12 leading-relaxed">
                Thoughtfully designed accessories and apparel for small to medium breeds who deserve to look as good as they feel. Meet Freddy, our fluffy style icon (often mistaken for a very fashionable sheep).
              </p>
              <Link to="/products">
                <Button size="lg" className="bg-neutral-900 hover:bg-neutral-800 text-white px-8 h-14 text-base">
                  Explore Collection
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_shopify-quick-build/artifacts/kixnd4fr_IMG_9433.jpeg"
                alt="Freddy the Bichon"
                className="rounded-lg w-full h-[600px] object-cover shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Meet Freddy - Simplified */}
      <section className="py-32 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-light text-neutral-900 mb-6">Meet Freddy</h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto leading-relaxed">
              Our brand ambassador, chief product tester, and part-time sheep impersonator. With his cloud-like coat and impeccable taste, Freddy ensures every piece meets the highest standards of comfort and style. (Yes, he's a Bichon Frisé, but we understand the confusion.)
            </p>
          </div>
          <div className="grid grid-cols-2 gap-6 max-w-3xl mx-auto">
            <img
              src="https://customer-assets.emergentagent.com/job_shopify-quick-build/artifacts/24pm9jct_B8A5F1BC-1E9B-4E19-A866-3D8A83D3BA58.png"
              alt="Freddy"
              className="rounded-lg w-full h-80 object-cover"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_shopify-quick-build/artifacts/aaqi7f2a_8E51361E-120C-488B-88EE-3367C81A5435.png"
              alt="Freddy"
              className="rounded-lg w-full h-80 object-cover"
            />
          </div>
          <div className="text-center mt-12">
            <a 
              href="https://www.instagram.com/freddythebichon" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="outline" size="lg" className="px-8 h-14 text-base">
                Follow @freddythebichon
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Categories - Cleaner, More Spacious */}
      <section className="py-32 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-light text-neutral-900 mb-6">Our Collections</h2>
            <p className="text-lg text-neutral-600">Three distinct categories for every need</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/products?category=Pet Accessories" className="group">
              <div className="relative overflow-hidden rounded-lg h-96 bg-neutral-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_shopify-quick-build/artifacts/efkamb99_IMG_7549.jpeg"
                  alt="Pet Accessories"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h3 className="text-2xl font-light mb-2">Pet Accessories</h3>
                  <p className="text-sm opacity-90">Jewelry · Bow Ties · Hats</p>
                </div>
              </div>
            </Link>
            <Link to="/products?category=Owner Accessories" className="group">
              <div className="relative overflow-hidden rounded-lg h-96 bg-neutral-100">
                <img
                  src="https://images.unsplash.com/photo-1625321515517-f72a45e3b164?w=600&q=80"
                  alt="Owner Accessories"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h3 className="text-2xl font-light mb-2">Owner Essentials</h3>
                  <p className="text-sm opacity-90">Poo Bags · Bottles · Holders</p>
                </div>
              </div>
            </Link>
            <Link to="/products?category=Clothing" className="group">
              <div className="relative overflow-hidden rounded-lg h-96 bg-neutral-100">
                <img
                  src="https://images.unsplash.com/photo-1548048026-5a1a941d93d3?w=600&q=80"
                  alt="Clothing"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h3 className="text-2xl font-light mb-2">Apparel</h3>
                  <p className="text-sm opacity-90">Coats · Rain Gear · Pajamas</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products - Minimal */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-light text-neutral-900 mb-6">Featured Products</h2>
            <p className="text-lg text-neutral-600">Curated selections from our collection</p>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            {featuredProducts.map((product) => (
              <div key={product.id} className="group">
                <Link to={`/product/${product.id}`}>
                  <div className="relative overflow-hidden rounded-lg mb-6 bg-neutral-50">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-96 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <h3 className="text-xl font-light text-neutral-900 mb-2">{product.name}</h3>
                  <p className="text-sm text-neutral-500 mb-4">{product.subcategory}</p>
                  <p className="text-2xl font-light text-neutral-900">£{product.price}</p>
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-16">
            <Link to="/products">
              <Button variant="outline" size="lg" className="px-8 h-14 text-base">
                View All Products
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
